<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="ra-header">
        <span class="ra-bar ra-bar--xl"></span>
        <span class="ra-bar ra-bar--md"></span>
      </div>
      <div class="ra-stage">
        <div
          v-for="(name, i) in names"
          :key="name"
          class="ra-pill"
          :class="{ '--picked': i === activeIndex }"
          :style="{ '--i': i }"
        >
          <span class="ra-dot"></span>
          {{ name }}
        </div>
      </div>
      <div class="ra-card">
        <span class="material-symbols-rounded ra-card-icon">redeem</span>
        <div class="ra-card-text">
          <span class="ra-bar ra-bar--md ra-bar--accent"></span>
          <span class="ra-bar ra-bar--lg"></span>
        </div>
      </div>
    </template>

    <template #audience>
      <span class="ra-phone-q"></span>
      <div class="ra-flip">
        <div class="ra-flip-inner">
          <div class="ra-flip-front">
            <span class="material-symbols-rounded ra-flip-icon">touch_app</span>
            <span class="ra-bar ra-bar--md ra-bar--white"></span>
          </div>
          <div class="ra-flip-back">
            <span class="material-symbols-rounded ra-flip-icon">redeem</span>
            <span class="ra-bar ra-bar--lg ra-bar--white"></span>
          </div>
        </div>
      </div>
      <span class="ra-phone-hint"></span>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewRandomAssignments',
  components: { PreviewFrame },
  props: { slide: { type: Object as PropType<SlideType>, required: true } },
  data() {
    return {
      names: ['Sam', 'Priya', 'Diego', 'Mei', 'Ana'],
      activeIndex: 2,
    }
  },
})
</script>

<style scoped lang="scss">
.ra-bar {
  display: block; height: 8px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.ra-bar--xl { width: 70%; background: rgba(15, 15, 20, 0.2); }
.ra-bar--lg { width: 55%; }
.ra-bar--md { width: 40%; }
.ra-bar--accent { background: var(--accent); }
.ra-bar--white { background: rgba(255, 255, 255, 0.6); }

.ra-header {
  display: flex; flex-direction: column; gap: 6px;
  padding-bottom: 8px;
  border-bottom: 1px dashed color-mix(in srgb, var(--accent) 20%, transparent);
}

.ra-stage {
  display: flex; flex-wrap: wrap; gap: 6px; padding: 6px 0;
}
.ra-pill {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 10px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.05);
  font-size: 11px;
  color: rgba(15, 15, 20, 0.6);
  font-weight: 600;
  animation: ra-shuffle 4s infinite;
  animation-delay: calc(var(--i, 0) * 60ms);
}
.ra-dot {
  width: 6px; height: 6px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.2);
}
.ra-pill.--picked {
  background: var(--accent);
  color: white;
  animation: ra-picked 4s infinite;
}
.ra-pill.--picked .ra-dot { background: rgba(255, 255, 255, 0.7); }

.ra-card {
  display: grid;
  grid-template-columns: 32px 1fr;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--accent) 10%, white);
  border: 1px solid color-mix(in srgb, var(--accent) 30%, transparent);
  animation: ra-card-in 4s infinite;
}
.ra-card-icon {
  width: 32px; height: 32px;
  display: grid; place-items: center;
  border-radius: 8px;
  background: var(--accent);
  color: white;
  font-size: 18px;
}
.ra-card-text { display: flex; flex-direction: column; gap: 5px; }

/* phone */
.ra-phone-q {
  display: block; height: 8px; width: 70%;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.18);
  margin-bottom: 4px;
}
.ra-flip {
  perspective: 600px;
  height: 90px;
  margin: 6px 0;
}
.ra-flip-inner {
  position: relative;
  width: 100%; height: 100%;
  transform-style: preserve-3d;
  animation: ra-flip 4s cubic-bezier(0.6, 0.05, 0.4, 0.95) infinite;
}
.ra-flip-front,
.ra-flip-back {
  position: absolute;
  inset: 0;
  border-radius: 12px;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 8px;
  backface-visibility: hidden;
}
.ra-flip-front {
  background: rgba(15, 15, 20, 0.06);
  border: 1px dashed rgba(15, 15, 20, 0.18);
}
.ra-flip-back {
  background: var(--accent);
  transform: rotateY(180deg);
  color: white;
}
.ra-flip-icon { font-size: 28px; color: rgba(15, 15, 20, 0.5); }
.ra-flip-back .ra-flip-icon { color: white; }
.ra-phone-hint {
  display: block; height: 6px; width: 50%;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.12);
  margin: 0 auto;
}

@keyframes ra-shuffle {
  0%, 60% { transform: translateY(0); opacity: 1; }
  70%     { transform: translateY(-2px); opacity: 0.6; }
  85%, 100% { transform: translateY(0); opacity: 1; }
}
@keyframes ra-picked {
  0%, 60%  { transform: scale(1); }
  70%      { transform: scale(1.08); }
  85%, 100% { transform: scale(1); }
}
@keyframes ra-card-in {
  0%, 70%   { opacity: 0; transform: translateY(8px); }
  82%, 100% { opacity: 1; transform: translateY(0); }
}
@keyframes ra-flip {
  0%, 45%   { transform: rotateY(0deg); }
  60%, 95%  { transform: rotateY(180deg); }
  100%      { transform: rotateY(0deg); }
}

@media (prefers-reduced-motion: reduce) {
  .ra-pill, .ra-card, .ra-flip-inner { animation: none; }
  .ra-flip-inner { transform: rotateY(180deg); }
}
</style>
